// src/hooks/useRequisicoesKanban.ts
import { useCallback, useState } from 'react';
import { apiGet } from '../services/api';
import { Contato } from '../types/contatos/Contato';
import { Requisicao } from '../types/compras/Requisicao';
import { usePaginatedFetch } from './usePaginatedFetch';
import { logger } from '@/utils/logger';

export const useRequisicoesKanban = () => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(null);

  const fetchContatos = useCallback(async (page: number) => {
    if (isSearchActive && !searchParams) {
      logger.error('Erro: isSearchActive é true, mas searchParams está indefinido');
      return { items: [] as Contato[], totalItems: 0 };
    }
    const endpoint = isSearchActive && searchParams
      ? `/v1/busca-inteligente/capa-contato-requisicao?page=${page}&size=10&${searchParams.toString()}`
      : `/v1/contatos-connection/capa?page=${page}&size=10`;
    const res = await apiGet(endpoint);
    const items: Contato[] = isSearchActive ? res.data.aguardandoAbertura?.content || [] : res.data.content || [];
    const totalItems = isSearchActive ? res.data.aguardandoAbertura?.page.totalElements || 0 : res.data.page.totalElements || 0;
    return { items, totalItems };
  }, [isSearchActive, searchParams]);

  const fetchPendentes = useCallback(async (page: number) => {
    if (isSearchActive && !searchParams) {
      logger.error('Erro: isSearchActive é true, mas searchParams está indefinido');
      return { items: [] as Requisicao[], totalItems: 0 };
    }
    const endpoint = isSearchActive && searchParams
      ? `/v1/busca-inteligente/capa-contato-requisicao?page=${page}&size=10&${searchParams.toString()}`
      : `/v1/requisicoes/capa?page=${page}&size=10&itens=pendente`;
    const res = await apiGet(endpoint);
    const items: Requisicao[] = isSearchActive ? res.data.pendentes?.content || [] : res.data.content || [];
    const totalItems = isSearchActive ? res.data.pendentes?.page.totalElements || 0 : res.data.page.totalElements || 0;
    return { items, totalItems };
  }, [isSearchActive, searchParams]);

  const fetchAprovar = useCallback(async (page: number) => {
    if (isSearchActive && !searchParams) {
      logger.error('Erro: isSearchActive é true, mas searchParams está indefinido');
      return { items: [] as Requisicao[], totalItems: 0 };
    }
    const endpoint = isSearchActive && searchParams
      ? `/v1/busca-inteligente/capa-contato-requisicao?page=${page}&size=10&${searchParams.toString()}`
      : `/v1/requisicoes/capa?page=${page}&size=10&itens=aprovar`;
    const res = await apiGet(endpoint);
    // busca-inteligente pode ainda nao expor a chave 'aprovar'; nesse caso
    // retorna lista vazia ate o backend incluir.
    const items: Requisicao[] = isSearchActive ? res.data.aprovar?.content || [] : res.data.content || [];
    const totalItems = isSearchActive ? res.data.aprovar?.page?.totalElements || 0 : res.data.page?.totalElements || 0;
    return { items, totalItems };
  }, [isSearchActive, searchParams]);

  const fetchEmProcesso = useCallback(async (page: number) => {
    if (isSearchActive && !searchParams) {
      logger.error('Erro: isSearchActive é true, mas searchParams está indefinido');
      return { items: [] as Requisicao[], totalItems: 0 };
    }
    const endpoint = isSearchActive && searchParams
      ? `/v1/busca-inteligente/capa-contato-requisicao?page=${page}&size=10&${searchParams.toString()}`
      : `/v1/requisicoes/capa?page=${page}&size=10&itens=emprocesso`;
    const res = await apiGet(endpoint);
    const items: Requisicao[] = isSearchActive ? res.data.emProcesso?.content || [] : res.data.content || [];
    const totalItems = isSearchActive ? res.data.emProcesso?.page.totalElements || 0 : res.data.page.totalElements || 0;
    return { items, totalItems };
  }, [isSearchActive, searchParams]);

  const fetchFinalizadas = useCallback(async (page: number) => {
    if (isSearchActive && !searchParams) {
      logger.error('Erro: isSearchActive é true, mas searchParams está indefinido');
      return { items: [] as Requisicao[], totalItems: 0 };
    }
    const endpoint = isSearchActive && searchParams
      ? `/v1/busca-inteligente/capa-contato-requisicao?page=${page}&size=10&${searchParams.toString()}`
      : `/v1/requisicoes/capa?page=${page}&size=10&itens=finalizado`;
    const res = await apiGet(endpoint);
    const items: Requisicao[] = isSearchActive ? res.data.finalizadas?.content || [] : res.data.content || [];
    const totalItems = isSearchActive ? res.data.finalizadas?.page.totalElements || 0 : res.data.page.totalElements || 0;
    return { items, totalItems };
  }, [isSearchActive, searchParams]);

  const contatos = usePaginatedFetch<Contato>({
    fetchFunction: fetchContatos,
    pageSize: 10,
  });

  const pendentes = usePaginatedFetch<Requisicao>({
    fetchFunction: fetchPendentes,
    pageSize: 10,
  });

  const aprovar = usePaginatedFetch<Requisicao>({
    fetchFunction: fetchAprovar,
    pageSize: 10,
  });

  const emProcesso = usePaginatedFetch<Requisicao>({
    fetchFunction: fetchEmProcesso,
    pageSize: 10,
  });

  const finalizadas = usePaginatedFetch<Requisicao>({
    fetchFunction: fetchFinalizadas,
    pageSize: 10,
  });

  // Refetch on search change é gerenciado pelo próprio usePaginatedFetch:
  // fetchXxx tem deps [isSearchActive, searchParams] → loadData muda → reset muda
  // → useEffect interno [reset, enabled] dispara refetch automaticamente.

  const reloadAllColumns = () => {
    contatos.reset();
    aprovar.reset();
    pendentes.reset();
    emProcesso.reset();
    finalizadas.reset();
  };

  const handleSearch = (_result: unknown, params: URLSearchParams) => {
    const cleanedParams = new URLSearchParams();
    params.forEach((value, key) => {
      if (key !== 'page' && key !== 'size') {
        cleanedParams.append(key, value);
      }
    });
    setSearchParams(cleanedParams);
    setIsSearchActive(true);
  };

  const handleResetSearch = () => {
    setSearchParams(null);
    setIsSearchActive(false);
  };

  return {
    contatos,
    aprovar,
    pendentes,
    emProcesso,
    finalizadas,
    isSearchActive,
    handleSearch,
    handleResetSearch,
    reloadAllColumns,
  };
};
